import { useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams, type To } from 'react-router-dom';

import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';

import { CountryPill } from '@/components/CountryPill';
import { RegistryOrderCloseModal } from '@/components/Modals/RegistryOrderCloseModal';
import { RegistryOrderModal } from '@/components/Modals/RegistryOrderModal';
import { RegistryOrderQuoteModal } from '@/components/Modals/RegistryOrderQuoteModal';
import { StatusBadge } from '@/components/StatusBadge';
import { armadilloAsideSearch, armadilloReplyTargetKey } from '@/hooks/useArmadilloAsideLink';
import { useRegistryOrderCloseDialog } from '@/hooks/useRegistryOrderCloseDialog';
import { useRegistryOrderQuoteDialog } from '@/hooks/useRegistryOrderQuoteDialog';
import { useRegistryOrders } from '@/hooks/useRegistryOrders';
import {
  useRegistryOrderSubjects,
  type RegistryOrderSubjectSource,
} from '@/hooks/useRegistryOrderSubjects';
import {
  describeRegistryOrderStatus,
  describeRegistryPrice,
  describeRegistryQuote,
  effectiveRegistryOrderStatus,
  isRegistryOrderAwaitingQuote,
  isRegistryOrderLate,
  isRegistryOrderOpen,
  isReportActivatedForOrders,
  isReportOrderable,
  mayHaveRegistryDocuments,
  REGISTRY_ORDER_STATUS_META,
  registryOrderCountry,
  registryOrderReasonLine,
  type RegistryOrder,
  type RegistryOrderDetail,
  type RegistryOrderMessage,
} from '@/types/registry-documents';
import type { ReportStatus } from '@/types/report';
import { formatDueDate } from '@/utils/businessDays';
import { RegistryOrderPanel } from './RegistryOrderPanel';
import { RegistryOrderReplyBadge } from './RegistryOrderReplyBadge';
import classes from './RegistryOrdersCard.module.css';

interface RegistryOrdersCardProps {
  reportId: string;
  /** Where the picker reads the report's company subjects from. */
  subjectSource: RegistryOrderSubjectSource;
  /** The report's stage — decides whether an order is sent at once or at
   * Start, and whether "Place now" is offered on a prepared order. Null
   * while the report has not loaded: the copy then promises neither. */
  reportStage: ReportStatus | null | undefined;
  /** Render a single line instead of an empty card when nothing has been
   * ordered — for pages where most reports never order anything. */
  compactWhenEmpty?: boolean;
  /** Whether the report offers new orders: Armadillo i-doc is locked on
   * one of its live partner assignments. False on a report shown only for
   * the orders it already has: nothing starts a new order, and the card
   * says what would; the existing orders keep every control they have. */
  canOrder?: boolean;
  /** An order's digest was added to the report's document, which is now
   * a version newer: the page re-reads the report it shows. */
  onReportContentChanged?: () => void;
}

/** What the card says when no live assignment is locked to Armadillo: the
 * lock is what brings new orders. */
const REGISTRY_ORDERS_OFF_NOTE = 'Lock Armadillo i-doc on a partner assignment to order documents.';

/** What the card says about when an order is sent, or that none can be.
 * A failed report's warning comes first even where new orders are off: a
 * prepared order's pill promises a send at Start that never comes. */
function timingFor(canOrder: boolean, orderable: boolean, activated: boolean | null): string {
  if (!orderable) {
    const failed = 'This report failed, so nothing prepared here would be sent.';
    return canOrder ? failed : `${failed} ${REGISTRY_ORDERS_OFF_NOTE}`;
  }
  if (!canOrder) {
    return REGISTRY_ORDERS_OFF_NOTE;
  }
  if (activated === null) {
    return 'Official company documents from Armadillo — sent at once if the report has started, otherwise when it starts.';
  }
  return activated
    ? 'Official company documents from Armadillo — a new order is sent at once.'
    : 'Official company documents from Armadillo — prepared now, sent when the report starts.';
}

/**
 * The registry documents ordered from Armadillo for one report, and the
 * way to order more.
 *
 * Prepared orders (made in triage) are sent at Start; the card keeps
 * polling so the analyst sees them move to placed and, later, delivered.
 * A prepared order can be discarded here; a sent one cannot — an order at
 * Armadillo is cancelled with its research desk, not from Nexus. Once that
 * is done (or a quote is declined), the order is closed here so it stops
 * counting as open.
 *
 * Where no live assignment is locked to Armadillo (`canOrder` false: the
 * card shows only because the report already has orders), the card keeps
 * those orders and everything done with them, offers nothing new, and
 * says that the lock is what brings ordering back. The page re-reads the
 * gate when a lock changes, so the card follows without a reload.
 *
 * Mount it with `key={reportId}` where the page survives a change of
 * report: the card's state belongs to one report.
 */
export function RegistryOrdersCard({
  reportId,
  subjectSource,
  reportStage,
  compactWhenEmpty = false,
  canOrder = true,
  onReportContentChanged,
}: Readonly<RegistryOrdersCardProps>) {
  const [modalOpened, setModalOpened] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Unknown stays unknown: the server decides whether an order is sent
  // now, and a guess here would promise the analyst the wrong thing.
  const activated = reportStage == null ? null : isReportActivatedForOrders(reportStage);
  const orderable = isReportOrderable(reportStage);

  const registry = useRegistryOrders(reportId, { reportActivated: activated });
  const subjects = useRegistryOrderSubjects(subjectSource, modalOpened);

  const openPicker = () => {
    setNotice(null);
    setModalOpened(true);
  };

  // The server decides when an order is sent. Say so when it sent one this
  // screen described as only prepared (the report started meanwhile).
  const onOrdered = (order: RegistryOrderDetail) => {
    if (activated !== true && order.status !== 'prepared') {
      setNotice(
        `${order.reference} was sent to Armadillo at once: the report has already started.`
      );
    }
  };

  const empty = registry.orders.length === 0;
  // Only the very first read hides the card. `error` survives retries, so
  // a card showing a load error stays put while the poll tries again.
  const firstRead = registry.isLoading && !registry.error && empty;

  let body: ReactNode;
  if (compactWhenEmpty && firstRead) {
    // No card while the first read is in flight on a page that collapses
    // when empty: most reports have nothing, and a full card that then
    // shrinks to one line reads as a glitch.
    body = null;
  } else if (compactWhenEmpty && empty && !registry.error && !notice) {
    body = <CompactLine canOrder={canOrder} orderable={orderable} onOpen={openPicker} />;
  } else {
    body = (
      <OrdersPanel
        registry={registry}
        timing={timingFor(canOrder, orderable, activated)}
        orderable={canOrder && orderable}
        placeable={activated === true}
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        onOpen={openPicker}
        onReportContentChanged={onReportContentChanged}
      />
    );
  }

  return (
    <>
      {body}
      {/* One position in the tree whichever body renders: moving the modal
          between parents would remount it mid-order and drop its state. */}
      <RegistryOrderModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        reportId={reportId}
        subjects={subjects.subjects}
        subjectsLoading={subjects.isLoading}
        subjectsError={subjects.error}
        reportActivated={activated}
        onSubmit={registry.create}
        onSubmitMany={registry.createMany}
        onOrdered={onOrdered}
      />
    </>
  );
}

function CompactLine({
  canOrder,
  orderable,
  onOpen,
}: Readonly<{ canOrder: boolean; orderable: boolean; onOpen: () => void }>) {
  let line = 'No registry documents ordered for this report.';
  if (!canOrder) {
    line = `${line} ${REGISTRY_ORDERS_OFF_NOTE}`;
  } else if (!orderable) {
    line = 'No registry documents ordered; this report failed, so none can be sent.';
  }
  return (
    <Group justify="space-between" data-testid="registry-orders-compact">
      <Text size="sm" c="dimmed">
        {line}
      </Text>
      {canOrder && orderable && (
        <Button size="xs" variant="light" onClick={onOpen} data-testid="registry-order-open">
          Order documents
        </Button>
      )}
    </Group>
  );
}

interface OrdersPanelProps {
  registry: ReturnType<typeof useRegistryOrders>;
  timing: string;
  /** "Order documents" is offered: Armadillo is locked on a live partner
   * assignment and the report has not failed. */
  orderable: boolean;
  /** "Place now" is offered on prepared orders: the report has started. */
  placeable: boolean;
  notice: string | null;
  onDismissNotice: () => void;
  onOpen: () => void;
  onReportContentChanged?: () => void;
}

/** The card once there is something to show. Exported for its story: it
 * takes the orders as data, where the card reads them itself. */
export function OrdersPanel({
  registry,
  timing,
  orderable,
  placeable,
  notice,
  onDismissNotice,
  onOpen,
  onReportContentChanged,
}: Readonly<OrdersPanelProps>) {
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyOrderId, setBusyOrderId] = useState<string | null>(null);
  const [confirmDiscardId, setConfirmDiscardId] = useState<string | null>(null);
  const { orders, isLoading, error, discard, place, close, recordQuote } = registry;
  const closeDialog = useRegistryOrderCloseDialog(close);
  const quoteDialog = useRegistryOrderQuoteDialog(recordQuote);

  // The desk is answered in Armadillo's conversation, in the page's aside:
  // the Reply controls open it there, pointed at the order or the email.
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const threadLink = (orderId: string, messageId?: string | null) => ({
    search: armadilloAsideSearch(armadilloReplyTargetKey(orderId, messageId), searchParams),
  });

  const runAction = async (orderId: string, action: () => Promise<unknown>) => {
    setBusyOrderId(orderId);
    setActionError(null);
    try {
      await action();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'The request failed');
    } finally {
      setBusyOrderId(null);
      setConfirmDiscardId(null);
    }
  };

  const onDiscard = (orderId: string) => {
    if (confirmDiscardId === orderId) {
      void runAction(orderId, () => discard(orderId));
    } else {
      setConfirmDiscardId(orderId);
    }
  };

  const empty = orders.length === 0;
  const openCount = orders.filter((order) =>
    isRegistryOrderOpen(effectiveRegistryOrderStatus(order))
  ).length;
  const anySent = orders.some((order) => order.status !== 'prepared');

  return (
    <Card withBorder padding="sm" radius="md" data-testid="registry-orders-card">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <Stack gap={0}>
            <Group gap="xs">
              <Title order={5}>Registry documents</Title>
              {!empty && (
                <Badge variant="light" color="blue" size="sm">
                  {openCount} open · {orders.length} total
                </Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {timing}
              {anySent &&
                ' A sent order is cancelled with Armadillo’s research desk, then closed here.'}
            </Text>
          </Stack>
          {orderable && (
            <Button size="xs" onClick={onOpen} data-testid="registry-order-open">
              Order documents
            </Button>
          )}
        </Group>

        {error && (
          <Alert color="red" title="Could not load the orders">
            {error.message}
          </Alert>
        )}
        {notice && (
          <Alert color="blue" withCloseButton onClose={onDismissNotice}>
            {notice}
          </Alert>
        )}
        {actionError && (
          <Alert color="red" withCloseButton onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        {isLoading && empty && (
          <Group>
            <Loader size="sm" />
            <Text size="sm">Loading orders…</Text>
          </Group>
        )}

        {!isLoading && empty && !error && (
          <Text size="sm" c="dimmed">
            {orderable
              ? 'Nothing ordered yet. Registry extracts, filings and accounts for a company subject can be ordered here; delivery takes from hours to days depending on the document.'
              : 'Nothing ordered for this report.'}
          </Text>
        )}

        {!empty && (
          <div className={classes.orders} role="list" aria-label="Registry document orders">
            {orders.map((order) => (
              <OrderRow
                key={order.id}
                order={order}
                activated={placeable}
                busy={busyOrderId === order.id}
                anyBusy={busyOrderId !== null}
                confirmingDiscard={confirmDiscardId === order.id}
                onDiscard={() => onDiscard(order.id)}
                onCancelDiscard={() => setConfirmDiscardId(null)}
                onPlace={() => void runAction(order.id, () => place(order.id))}
                onCloseOrder={() => closeDialog.open(order)}
                onReplyToMessage={(message) => navigate(threadLink(order.id, message.messageId))}
                replyLink={threadLink(order.id)}
                onRecordQuote={() => quoteDialog.open(order)}
                onReportContentChanged={onReportContentChanged}
              />
            ))}
          </div>
        )}
      </Stack>
      {/* One dialog of each kind for the card rather than one per row: it
          must outlive whatever the next poll does to the row it was opened
          from. */}
      <RegistryOrderCloseModal {...closeDialog.modalProps} />
      <RegistryOrderQuoteModal {...quoteDialog.modalProps} />
    </Card>
  );
}

// ---------------------------------------------------------------------------

function OrderRow({
  order,
  activated,
  busy,
  anyBusy,
  confirmingDiscard,
  onDiscard,
  onCancelDiscard,
  onPlace,
  onCloseOrder,
  onReplyToMessage,
  replyLink,
  onRecordQuote,
  onReportContentChanged,
}: Readonly<{
  order: RegistryOrder;
  activated: boolean;
  busy: boolean;
  anyBusy: boolean;
  confirmingDiscard: boolean;
  onDiscard: () => void;
  onCancelDiscard: () => void;
  onPlace: () => void;
  onCloseOrder: () => void;
  /** Open the conversation on one of the desk's messages on this order. */
  onReplyToMessage: (message: RegistryOrderMessage) => void;
  /** Where the conversation is, pointed at this order: the Reply pill. */
  replyLink: To;
  onRecordQuote: () => void;
  onReportContentChanged?: () => void;
}>) {
  const [documentsOpen, setDocumentsOpen] = useState(false);
  // What arrived decides what is shown: the worker may not be allowed to
  // move the status itself (shadow mode).
  const status = effectiveRegistryOrderStatus(order);
  const meta = REGISTRY_ORDER_STATUS_META[status];
  const receiving = status === 'placed' || status === 'partially_delivered';
  // An unconfirmed send that documents arrived for was live after all.
  const counted =
    receiving ||
    status === 'delivered' ||
    (order.status === 'pending' && order.receivedDocumentCount > 0);
  const late = isRegistryOrderLate(order);
  // A quote the desk gave, once recorded, stands where the list price would.
  const price =
    describeRegistryQuote(order.quote) ??
    describeRegistryPrice(order.listPrice, order.currency, order.requiresQuote);
  // Why the order ended without its documents: it failed, or a person
  // closed it. A deliberate close is not an error, so it is not red.
  const reasonLine = registryOrderReasonLine(order);

  // A closed order expects nothing, whatever date it once had.
  const expectedAt = order.status === 'cancelled' ? null : order.expectedAt;
  let expected: string | null = null;
  if (expectedAt) {
    expected = `Expected ${formatDueDate(expectedAt)}`;
  } else if (isRegistryOrderAwaitingQuote(order)) {
    expected = 'Expected after quote';
  }
  const facts = [order.products.join(', '), price, expected].filter(Boolean).join(' · ');

  return (
    <div className={classes.order} role="listitem" data-testid={`registry-order-${order.id}`}>
      <Stack gap={4} className={classes.summary}>
        <Group gap={6} wrap="wrap">
          <Text size="sm" fw={500}>
            {order.companyName}
          </Text>
          <CountryPill country={registryOrderCountry(order.countryCode)} />
          {order.manual && (
            <Tooltip label="Not in Armadillo's index — ordered by name for their research desk to identify">
              <Badge size="xs" variant="light" color="grape">
                by name
              </Badge>
            </Tooltip>
          )}
          <StatusBadge
            label={meta.label}
            color={meta.color}
            tooltip={describeRegistryOrderStatus(
              { status, failureReason: order.failureReason },
              activated
            )}
          />
          {late && (
            <Tooltip
              label={`Expected ${order.expectedAt ? formatDueDate(order.expectedAt) : ''}. Check Documents for files not yet matched, then chase Armadillo's research desk quoting ${order.reference}.`}
            >
              <Badge size="sm" variant="light" color="orange">
                Late
              </Badge>
            </Tooltip>
          )}
          <RegistryOrderReplyBadge order={order} to={replyLink} />
          {counted && order.expectedDocumentCount > 0 && (
            <Text size="xs" c="dimmed">
              {order.receivedDocumentCount}/{order.expectedDocumentCount} received
            </Text>
          )}
        </Group>
        <Text size="xs" c="dimmed">
          <Text span inherit ff="monospace">
            {order.reference}
          </Text>
          {facts && ` · ${facts}`}
        </Text>
        {reasonLine && (
          <Text size="xs" c={order.status === 'failed' ? 'red' : 'dimmed'}>
            {reasonLine}
          </Text>
        )}
      </Stack>
      {mayHaveRegistryDocuments(order.status) && (
        <Button
          size="compact-xs"
          variant="subtle"
          className={classes.actions}
          onClick={() => setDocumentsOpen((open) => !open)}
          aria-expanded={documentsOpen}
          data-testid={`registry-order-documents-toggle-${order.id}`}
        >
          {documentsOpen ? 'Hide documents' : 'Documents'}
        </Button>
      )}
      {documentsOpen && (
        <div className={classes.documents} data-testid={`registry-order-documents-${order.id}`}>
          <RegistryOrderPanel
            order={order}
            onCloseOrder={onCloseOrder}
            onReplyToMessage={onReplyToMessage}
            onRecordQuote={onRecordQuote}
            onReportContentChanged={onReportContentChanged}
          />
        </div>
      )}
      {order.status === 'prepared' && (
        <Group gap={4} wrap="nowrap" className={classes.actions}>
          {confirmingDiscard ? (
            <>
              <Button
                size="compact-xs"
                color="red"
                onClick={onDiscard}
                loading={busy}
                disabled={anyBusy && !busy}
                data-testid={`registry-order-discard-confirm-${order.id}`}
              >
                Confirm discard
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                onClick={onCancelDiscard}
                disabled={busy}
              >
                Keep
              </Button>
            </>
          ) : (
            <>
              {activated && (
                <Tooltip label="Send it to Armadillo now instead of waiting for the next retry">
                  <Button
                    size="compact-xs"
                    variant="light"
                    onClick={onPlace}
                    loading={busy}
                    disabled={anyBusy && !busy}
                    data-testid={`registry-order-place-${order.id}`}
                  >
                    Place now
                  </Button>
                </Tooltip>
              )}
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                onClick={onDiscard}
                disabled={anyBusy}
                data-testid={`registry-order-discard-${order.id}`}
              >
                Discard
              </Button>
            </>
          )}
        </Group>
      )}
    </div>
  );
}
